/**
 * Snapshot fields available for condition building per trigger type.
 * Used by ConditionEditorSheet to populate field selectors.
 */

export type FieldType = 'number' | 'string' | 'enum';

export interface ConditionField {
  value: string;
  label: string;
  type: FieldType;
  options?: string[];
}

/** Fields available on every trigger snapshot. */
const COMMON_FIELDS: ConditionField[] = [
  { value: 'energy_remaining_kwh', label: 'Energy Remaining (kWh)', type: 'number' },
  { value: 'reserve_kwh', label: 'Reserve Threshold (kWh)', type: 'number' },
  { value: 'confidence', label: 'Forecast Confidence', type: 'enum', options: ['low', 'medium', 'high'] },
  { value: 'property_name', label: 'Property Name', type: 'string' },
];

/** Per-trigger additional fields. */
const TRIGGER_FIELDS: Record<string, ConditionField[]> = {
  reserve_breach_imminent: [
    { value: 'time_to_reserve_minutes', label: 'Time to Reserve (min)', type: 'number' },
    { value: 'net_draw_kw', label: 'Net Draw (kW)', type: 'number' },
  ],
  overnight_risk: [
    { value: 'projected_kwh_at_sunrise', label: 'Projected kWh at Sunrise', type: 'number' },
  ],
  resilience_risk: [
    { value: 'runtime_hours', label: 'Backup Runtime (hours)', type: 'number' },
    { value: 'forecast_kWh', label: 'Forecast Solar (kWh)', type: 'number' },
    { value: 'daily_load_kWh', label: 'Expected Load (kWh)', type: 'number' },
  ],
  resilience_risk_critical: [
    { value: 'runtime_hours', label: 'Backup Runtime (hours)', type: 'number' },
    { value: 'forecast_kWh', label: 'Forecast Solar (kWh)', type: 'number' },
    { value: 'daily_load_kWh', label: 'Expected Load (kWh)', type: 'number' },
  ],
  system_offline: [
    { value: 'last_update_minutes', label: 'Minutes Since Last Update', type: 'number' },
  ],
  data_stale_low_energy: [
    { value: 'last_update_minutes', label: 'Minutes Since Last Update', type: 'number' },
  ],
  battery_critically_low: [
    { value: 'current_soc', label: 'Current SOC (%)', type: 'number' },
  ],
  pv_curtailment: [
    { value: 'wasted_power_w', label: 'Wasted Power (W)', type: 'number' },
    { value: 'wasted_today_kwh', label: 'Wasted Today (kWh)', type: 'number' },
  ],
  forecast_confidence_low: [],
};

/** Get all condition fields available for a given trigger type. */
export function getConditionFields(triggerType: string): ConditionField[] {
  return [...COMMON_FIELDS, ...(TRIGGER_FIELDS[triggerType] ?? [])];
}

import type { ConditionOperator } from '@/lib/dag-types';

/** Operators available per field type. */
export function getOperatorsForType(fieldType: FieldType): { value: ConditionOperator; label: string }[] {
  const base: { value: ConditionOperator; label: string }[] = [
    { value: 'eq', label: '=' },
    { value: 'neq', label: '!=' },
  ];
  if (fieldType === 'number') {
    return [
      ...base,
      { value: 'gt', label: '>' },
      { value: 'gte', label: '>=' },
      { value: 'lt', label: '<' },
      { value: 'lte', label: '<=' },
      { value: 'between', label: 'between' },
    ];
  }
  if (fieldType === 'enum') {
    return [...base, { value: 'in', label: 'in' }];
  }
  // string
  return [...base, { value: 'in', label: 'in' }];
}
